import { type RoomHistoryLink, type UserProfile } from '@big2/shared';
import { type ClientState, type Action } from './types.js';

function savedProfile(): UserProfile | null {
  if (typeof localStorage === 'undefined' || typeof localStorage?.getItem !== 'function') return null;
  try {
    const raw = localStorage.getItem('big2.profile');
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (p && typeof p.id === 'string' && typeof p.email === 'string') {
      return p as UserProfile;
    }
  } catch {
    return null;
  }
  return null;
}

function savedRoomHistoryLinks(): RoomHistoryLink[] {
  try {
    const saved: unknown = JSON.parse(localStorage.getItem('big2.roomHistory') ?? '[]');
    if (!Array.isArray(saved)) return [];
    return saved.filter((link: any) => link && typeof link.id === 'string' && /^[0-9a-f-]{36}$/i.test(link.id) &&
      typeof link.key === 'string' && /^[0-9a-f-]{36}$/i.test(link.key) && typeof link.roomId === 'string' &&
      typeof link.startedAt === 'string' && Number.isFinite(Date.parse(link.startedAt))).slice(0, 50);
  } catch { return []; }
}

export function buildInitialState(): ClientState {
  return {
    ...initialState,
    sessionId: typeof localStorage !== 'undefined' && typeof localStorage?.getItem === 'function' ? localStorage.getItem('big2.session') : null,
    lastRoomId: typeof localStorage !== 'undefined' && typeof localStorage?.getItem === 'function' ? localStorage.getItem('big2.lastRoom') : null,
    authToken: typeof localStorage !== 'undefined' && typeof localStorage?.getItem === 'function' ? localStorage.getItem('big2.authToken') : null,
    refreshToken: typeof localStorage !== 'undefined' && typeof localStorage?.getItem === 'function' ? localStorage.getItem('big2.refreshToken') : null,
    profile: (typeof localStorage !== 'undefined' && typeof localStorage?.getItem === 'function' && localStorage.getItem('big2.authToken')) ? savedProfile() : null,
    roomHistoryLinks: savedRoomHistoryLinks(),
  };
}

export const initialState: ClientState = {
  supportedGames: ['big2', 'muushig', 'daaluu'],
  connectionStatus: 'connecting',
  latencyMs: null,
  error: null,
  authNotice: null,
  verificationRequired: false,
  pendingVerificationEmail: null,
  recoveryToken: null,
  sessionId: typeof localStorage !== 'undefined' && typeof localStorage?.getItem === 'function' ? localStorage.getItem('big2.session') : null,
  lastRoomId: typeof localStorage !== 'undefined' && typeof localStorage?.getItem === 'function' ? localStorage.getItem('big2.lastRoom') : null,
  authToken: typeof localStorage !== 'undefined' && typeof localStorage?.getItem === 'function' ? localStorage.getItem('big2.authToken') : null,
  refreshToken: typeof localStorage !== 'undefined' && typeof localStorage?.getItem === 'function' ? localStorage.getItem('big2.refreshToken') : null,
  feedbackSubmittedId: null,
  profile: (typeof localStorage !== 'undefined' && typeof localStorage?.getItem === 'function' && localStorage.getItem('big2.authToken')) ? savedProfile() : null,
  billingHistory: null,
  couponQuote: null,
  adminData: null,
  adminSaved: null,
  roomHistoryLinks: savedRoomHistoryLinks(),
  roomHistory: null,
  weeklyBonusNotice: null,
  roomId: null,
  playerId: null,
  lobby: null,
  publicRooms: [],
  matchmaking: null,
  game: null,
  lastGameOver: null,
  reactions: {},
  tableThrows: [],
  voiceTaunts: {},
  voiceEnabled: false,
  voiceParticipants: [],
  paymentInfo: null,
  leaderboard: null,
  leaderboardLoading: false,
  friendsData: null,
  friendsLoading: false,
  inspectedPlayer: null,
  inspectingPlayerLoading: false,
  matchLoading: null,
  serverOffsetMs: 0,
  serverTimeCalibrated: false,
};

export function reducer(state: ClientState, action: Action): ClientState {
  switch (action.type) {
    case 'CAPABILITIES':
      return { ...state, supportedGames: [...action.supportedGames] };
    case 'ROOM_HISTORY':
      return { ...state, roomHistory: action.payload };
    case 'ROOM_HISTORY_LOADING':
      return { ...state, roomHistory: null, error: null };
    case 'BILLING_HISTORY':
      return state.profile ? {
        ...state,
        billingHistory: action.payload,
        paymentInfo: action.payload.some(payment => payment.invoice_id === state.paymentInfo?.invoiceId && payment.status === 'paid') ? null : state.paymentInfo,
      } : state;
    case 'COUPON_QUOTE':
      return state.profile ? { ...state, couponQuote: action.payload, error: null } : state;
    case 'CLEAR_COUPON_QUOTE':
      return { ...state, couponQuote: null };
    case 'ADMIN_DATA':
      return state.profile?.isAdmin ? { ...state, adminData: action.payload } : state;
    case 'ADMIN_SAVED':
      return { ...state, adminSaved: action.requestId };
    case 'ADMIN_LOADING':
      return { ...state, adminData: null, error: null };
    case 'CONNECTED':
      return { ...state, connectionStatus: 'connected' };
    case 'DISCONNECTED':
      return {
        ...state,
        connectionStatus: 'disconnected',
        leaderboardLoading: false,
        friendsLoading: false,
        inspectingPlayerLoading: false,
      };
    case 'LATENCY':
      return {
        ...state,
        latencyMs: action.ms,
        ...(action.serverOffsetMs !== undefined
          ? { serverOffsetMs: action.serverOffsetMs, serverTimeCalibrated: true }
          : {}),
      };
    case 'SERVER_TIME_SYNC':
      return {
        ...state,
        serverOffsetMs: action.serverOffsetMs,
        serverTimeCalibrated: true,
      };
    case 'ERROR':
      return {
        ...state,
        error: action.message,
        couponQuote: null,
        leaderboardLoading: false,
        friendsLoading: false,
        inspectingPlayerLoading: false,
      };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    case 'AUTH_STATE': {
      const nextProfile = action.payload.profile && action.payload.profile.id === state.profile?.id
        ? { ...state.profile, ...action.payload.profile }
        : action.payload.profile;
      const nextToken =
        action.payload.authToken !== undefined ? action.payload.authToken : state.authToken;
      const nextRefreshToken = action.payload.authToken === null
        ? null
        : action.payload.refreshToken !== undefined ? action.payload.refreshToken : state.refreshToken;
      if (typeof localStorage !== 'undefined') {
        if (nextToken) localStorage.setItem('big2.authToken', nextToken);
        else if (action.payload.authToken === null) localStorage.removeItem('big2.authToken');
        if (nextRefreshToken) localStorage.setItem('big2.refreshToken', nextRefreshToken);
        else if (action.payload.authToken === null || action.payload.refreshToken === null) localStorage.removeItem('big2.refreshToken');
        if (nextProfile) {
          localStorage.setItem('big2.profile', JSON.stringify(nextProfile));
          if (nextProfile.displayName) {
            localStorage.setItem('big2.name', nextProfile.displayName);
          }
        } else if (action.payload.profile === null) {
          localStorage.removeItem('big2.profile');
        }
      }
      return {
        ...state,
        profile: nextProfile,
        verificationRequired:
          action.payload.verificationRequired !== undefined
            ? action.payload.verificationRequired
            : nextProfile
            ? false
            : state.verificationRequired,
        pendingVerificationEmail:
          action.payload.pendingEmail !== undefined
            ? action.payload.pendingEmail
            : nextProfile
            ? null
            : state.pendingVerificationEmail,
        ...(nextProfile ? { recoveryToken: null } : {}),
        ...(action.payload.profile?.id !== state.profile?.id ? { billingHistory: null, couponQuote: null, adminData: null, adminSaved: null, paymentInfo: null, friendsData: null } : {}),
        ...(action.payload.profile?.subscriptionExpiresAt !== state.profile?.subscriptionExpiresAt ? { couponQuote: null } : {}),
        ...(!nextProfile?.isAdmin ? { adminData: null, adminSaved: null } : {}),
        authToken: nextToken ?? null,
        refreshToken: nextRefreshToken ?? null,
        weeklyBonusNotice: action.payload.weeklyBonusGranted || state.weeklyBonusNotice,
        error: null,
      };
    }
    case 'AUTH_NOTICE': {
      const n = action.payload;
      return {
        ...state,
        authNotice: n,
        error: null,
        ...(n.kind === 'verification_sent' && n.email ? { pendingVerificationEmail: n.email } : {}),
        ...(n.kind === 'password_reset_sent' && n.email ? { pendingVerificationEmail: n.email } : {}),
        ...(n.kind === 'password_reset_ready'
          ? {
              recoveryToken: n.recoveryToken ?? state.recoveryToken,
              pendingVerificationEmail: n.email ?? state.pendingVerificationEmail,
            }
          : {}),
        ...(n.kind === 'password_updated' ? { recoveryToken: null } : {}),
        ...(n.kind === 'email_verified'
          ? { verificationRequired: false, pendingVerificationEmail: null }
          : {}),
      };
    }
    case 'CLEAR_AUTH_NOTICE':
      return { ...state, authNotice: null };
    case 'CLEAR_WEEKLY_NOTICE':
      return { ...state, weeklyBonusNotice: null };
    case 'ROOM_JOINED': {
      const link = action.payload.history;
      const roomHistoryLinks = link ? [link, ...state.roomHistoryLinks.filter(item => item.id !== link.id)].slice(0, 50) : state.roomHistoryLinks;
      if (link) {
        try { localStorage.setItem('big2.roomHistory', JSON.stringify(roomHistoryLinks)); } catch { /* The current tab still retains access. */ }
      }
      try {
        if (typeof localStorage !== 'undefined' && typeof localStorage.setItem === 'function') {
          localStorage.setItem('big2.session', action.payload.sessionId);
          localStorage.setItem('big2.lastRoom', action.payload.roomId);
        }
      } catch {
        // Ignore storage access errors
      }
      return {
        ...state,
        sessionId: action.payload.sessionId,
        lastRoomId: action.payload.roomId,
        roomId: action.payload.roomId,
        roomHistoryLinks,
        playerId: action.payload.playerId,
        matchmaking: null,
        tableThrows: [],
        voiceTaunts: {},
        error: null,
      };
    }
    case 'LOBBY_UPDATE':
      if (!state.roomId) return state;
      return {
        ...state,
        lobby: action.payload,
        voiceEnabled: Boolean(action.payload.config.voiceChatEnabled),
        voiceParticipants: action.payload.config.voiceChatEnabled ? state.voiceParticipants : [],
        tableThrows: action.payload.phase === 'lobby' ? [] : state.tableThrows,
        voiceTaunts: action.payload.phase === 'lobby' ? {} : state.voiceTaunts,
        game: action.payload.phase === 'lobby' ? null : state.game ? { ...state.game, totals: action.payload.totals ? { ...action.payload.totals } : state.game.totals } : state.game,
        lastGameOver: action.payload.phase === 'playing' ? null : state.lastGameOver,
        matchLoading: action.payload.phase === 'lobby' ? null : state.matchLoading,
      };
    case 'PUBLIC_ROOMS':
      return {
        ...state,
        publicRooms: action.payload,
      };
    case 'MATCHMAKING_STATUS':
      return {
        ...state,
        matchmaking: action.payload.status === 'cancelled' ? null : action.payload,
      };
    case 'CLEAR_MATCHMAKING':
      return {
        ...state,
        matchmaking: null,
      };
    case 'SYNC_GAME_STATE':
      if (!state.roomId) return state;
      const initialOffset =
        !state.serverTimeCalibrated && typeof action.payload.serverTime === 'number'
          ? action.payload.serverTime - Date.now()
          : state.serverOffsetMs;
      return {
        ...state,
        serverOffsetMs: initialOffset,
        game: action.payload,
        lastGameOver: action.payload.phase === 'playing' ? null : state.lastGameOver,
      };
    case 'GAME_OVER':
      return {
        ...state,
        lastGameOver: action.payload,
        matchLoading: null,
        game: state.game
          ? {
              ...state.game,
              totals: action.payload.totals ? { ...action.payload.totals } : state.game.totals,
            }
          : state.game,
      };
    case 'CLEAR_GAME_OVER':
      return {
        ...state,
        lastGameOver: null,
      };
    case 'SET_MATCH_LOADING':
      return {
        ...state,
        matchLoading: action.payload,
      };
    case 'CLEAR_MATCH_LOADING':
      return {
        ...state,
        matchLoading: null,
      };
    case 'PLAYER_REACTION':
      return {
        ...state,
        reactions: {
          ...state.reactions,
          [action.payload.playerId]: action.payload,
        },
      };
    case 'CLEAR_REACTION': {
      const current = state.reactions[action.playerId];
      if (!current || current.seq !== action.seq) return state;
      const next = { ...state.reactions };
      delete next[action.playerId];
      return { ...state, reactions: next };
    }
    case 'TABLE_THROW':
      return {
        ...state,
        tableThrows: [...state.tableThrows.slice(-19), action.payload],
      };
    case 'CLEAR_TABLE_THROW':
      return {
        ...state,
        tableThrows: state.tableThrows.filter((t) => t.seq !== action.seq),
      };
    case 'VOICE_TAUNT':
      return {
        ...state,
        voiceTaunts: {
          ...state.voiceTaunts,
          [action.payload.fromPlayerId]: action.payload,
        },
      };
    case 'CLEAR_VOICE_TAUNT': {
      const next = { ...state.voiceTaunts };
      let changed = false;
      for (const [k, v] of Object.entries(next)) {
        if (v.seq === action.seq) {
          delete next[k];
          changed = true;
        }
      }
      return changed ? { ...state, voiceTaunts: next } : state;
    }
    case 'VOICE_PARTICIPANTS':
      if (!state.roomId || action.payload.roomId !== state.roomId) return state;
      return {
        ...state,
        voiceEnabled: action.payload.voiceEnabled,
        voiceParticipants: action.payload.participants,
      };
    case 'SESSION_EXPIRED':
      try {
        if (typeof localStorage !== 'undefined' && typeof localStorage.removeItem === 'function') {
          localStorage.removeItem('big2.session');
          localStorage.removeItem('big2.lastRoom');
        }
      } catch {
        // Ignore
      }
      return {
        ...state,
        sessionId: null,
        lastRoomId: null,
        roomId: null,
        playerId: null,
        lobby: null,
        game: null,
        lastGameOver: null,
        error: null,
        reactions: {},
        tableThrows: [],
        voiceTaunts: {},
        voiceEnabled: false,
        voiceParticipants: [],
      };
    case 'PLAYER_KICKED':
      try {
        if (typeof localStorage !== 'undefined' && typeof localStorage.removeItem === 'function') {
          localStorage.removeItem('big2.session');
          localStorage.removeItem('big2.lastRoom');
        }
      } catch {
        // Ignore
      }
      return {
        ...state,
        sessionId: null,
        lastRoomId: null,
        roomId: null,
        playerId: null,
        lobby: null,
        game: null,
        lastGameOver: null,
        error: action.message,
        reactions: {},
        tableThrows: [],
        voiceTaunts: {},
        voiceEnabled: false,
        voiceParticipants: [],
      };
    case 'LEAVE_ROOM': {
      try {
        if (typeof localStorage !== 'undefined' && typeof localStorage.removeItem === 'function') {
          localStorage.removeItem('big2.session');
          // Note: big2.lastRoom is preserved so the player can voluntarily click
          // "Rejoin Match" from the home page banner if they wish to go back.
        }
      } catch {
        // Ignore
      }
      return {
        ...state,
        sessionId: null,
        lastRoomId: state.roomId ?? state.lastRoomId,
        roomId: null,
        playerId: null,
        lobby: null,
        game: null,
        lastGameOver: null,
        error: null,
        reactions: {},
        tableThrows: [],
        voiceTaunts: {},
        voiceEnabled: false,
        voiceParticipants: [],
      };
    }
    case 'PAYMENT_INFO':
      return { ...state, paymentInfo: action.payload };
    case 'CLEAR_PAYMENT_INFO':
      return { ...state, paymentInfo: null };
    case 'LEADERBOARD_DATA':
      return { ...state, leaderboard: action.payload, leaderboardLoading: false };
    case 'LEADERBOARD_LOADING':
      return { ...state, leaderboardLoading: true };
    case 'FRIENDS_DATA':
      return { ...state, friendsData: action.payload, friendsLoading: false };
    case 'FRIENDS_LOADING':
      return { ...state, friendsLoading: true };
    case 'PLAYER_PROFILE':
      return { ...state, inspectedPlayer: action.payload, inspectingPlayerLoading: false };
    case 'PLAYER_PROFILE_LOADING':
      return { ...state, inspectingPlayerLoading: true };
    case 'CLEAR_INSPECTED_PLAYER':
      return { ...state, inspectedPlayer: null, inspectingPlayerLoading: false };
    case 'FEEDBACK_SUBMITTED':
      return { ...state, feedbackSubmittedId: action.id, error: null };
    case 'CLEAR_FEEDBACK_NOTICE':
      return { ...state, feedbackSubmittedId: null };
    default:
      return state;
  }
}
